// frontend/src/pages/Clientes.jsx
import React, { useEffect, useState } from 'react';
import {
  Container,
  Button,
  Stack,
  Typography,
  Paper,
} from '@mui/material';
import DataTable from '../components/DataTable';
import AppPagination from '../components/AppPagination';
import { fetchClientes, deleteCliente } from '../api/clientes';
import { useNavigate } from 'react-router-dom';
import { useLoading } from '../contexts/LoadingContext';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';

export default function Clientes() {
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Estado de paginación del API (1-based).
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);

  const nav = useNavigate();
  const { start, stop } = useLoading();
  const { showToast } = useToast();
  const confirm = useConfirm();

  const loadClientes = async (currentPage = page, limit = rowsPerPage) => {
    setLoading(true);
    try {
      const res = await fetchClientes(currentPage, limit);
      const payload = res?.data ?? [];
      setClientes(Array.isArray(payload) ? payload : []);
      setPage(res?.page ?? currentPage);
      setTotalRows(res?.total ?? payload.length);
    } catch (err) {
      console.error('Error cargando clientes', err);
      showToast({ message: 'Error cargando clientes', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        await loadClientes(1, rowsPerPage);
      } finally {
        stop();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeRowsPerPage = async (event) => {
    const newLimit = Number(event.target.value);
    setRowsPerPage(newLimit);
    await loadClientes(1, newLimit);
  };

  const handleChangePage = async (_event, nextPage) => {
    await loadClientes(nextPage, rowsPerPage);
  };

  const goToNuevo = () => {
    start();
    nav('/clientes/nuevo');
  };

  const goToEditar = (id) => {
    start();
    nav(`/clientes/editar/${id}`);
  };

  const handleDelete = async (row) => {
    const ok = await confirm({
      title: 'Eliminar cliente',
      content: `¿Eliminar el cliente "${row.nombre}"?`,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      confirmColor: 'error',
    });
    if (!ok) return;

    start();
    try {
      await deleteCliente(row.id);
      showToast({ message: 'Cliente eliminado', severity: 'success' });

      await loadClientes(page, rowsPerPage);
      if (clientes.length === 1 && page > 1) {
        await loadClientes(page - 1, rowsPerPage);
      }
    } catch (err) {
      console.error('Error eliminando cliente', err);
      showToast({ message: 'No se pudo eliminar el cliente', severity: 'error' });
    } finally {
      stop();
    }
  };

  const columns = [
    { Header: 'ID', accessor: 'id' },
    { Header: 'Nombre', accessor: 'nombre' },
    { Header: 'Contacto', accessor: 'contacto' },
    { Header: 'Email', accessor: 'email' },
    { Header: 'Dirección', accessor: 'direccion' },
  ];

  return (
    <Container sx={{ mt: 4 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h5">Clientes</Typography>
        <Button variant="contained" onClick={goToNuevo}>
          Nuevo cliente
        </Button>
      </Stack>

      <Paper>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
          sx={{ p: 2 }}
        >
          <Typography variant="body2">
            {loading
              ? 'Cargando...'
              : `Mostrando ${clientes.length} de ${totalRows} clientes`}
          </Typography>
        </Stack>

        <DataTable
          rows={clientes}
          columns={columns}
          loading={loading}
          onEdit={(row) => goToEditar(row.id)}
          onDelete={(row) => handleDelete(row)}
        />

        <AppPagination
          count={totalRows}
          page={Math.max(0, page - 1)}
          rowsPerPage={rowsPerPage}
          onPageChange={(event, nextPage) => handleChangePage(event, nextPage + 1)}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Container>
  );
}
